"use client";

import * as Popover from "@radix-ui/react-popover";
import { motion, useReducedMotion } from "framer-motion";
import { forwardRef, type ReactNode } from "react";

import FetchModal from "@/fetch-ui/FetchModal";
import { useMobile } from "@/hooks/use-mobile";

import { POPOVER_MOTION, SURFACE_FEEL } from "../../_logic/motion";

/** What the segment's trigger reads. */
interface SegmentContent {
  id?: string;
  /** Segment title, e.g. "Who". Omitted for the trailing options control. */
  title?: string;
  /** Muted text after the title, e.g. "3 nights". */
  titleDetail?: string | null;
  value: string | null;
  placeholder: string;
  /** The chat set this value last. */
  byChat?: boolean;
}

/** Open and focus state, owned by the trip bar so one segment is open at a time. */
interface SegmentState {
  open: boolean;
  /** The white pill sits under this segment. */
  active: boolean;
  onOpenChange: (open: boolean) => void;
  onFocusChange: (focused: boolean) => void;
}

interface TripSegmentProps {
  content: SegmentContent;
  state: SegmentState;
  /** The popover panel's width class, or its width in pixels (e.g. the stacked bar's), which wins. */
  panel?: { className?: string; width?: number | null };
  /** Layout classes for the trigger, e.g. a fixed-width trailing segment. */
  className?: string;
  trailing?: ReactNode;
  children: ReactNode;
}

/**
 * One segment of the trip bar: a title over its value ("Who" / "11 people · San Francisco"),
 * opening a popover with the full editor. Closes on outside press and Escape.
 * @param props - Title, value, open and focus state, and the editor.
 * @returns The segment.
 * @throws Never.
 */
export const TripSegment = forwardRef<HTMLButtonElement, TripSegmentProps>(function TripSegment(
  { content, state, panel = {}, className = "flex-1", trailing, children },
  ref
) {
  const { id, title, titleDetail, value, placeholder, byChat = false } = content;
  const { open, active, onOpenChange, onFocusChange } = state;
  const { className: panelClass = "w-[360px]", width: panelWidth = null } = panel;
  const reduceMotion = useReducedMotion();
  const isMobile = useMobile();
  const trigger = (
    // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
    <button
      ref={ref}
      id={id}
      type="button"
      onClick={isMobile ? () => onOpenChange(!open) : undefined}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
      className={`relative z-10 flex min-w-0 items-center gap-2 self-stretch rounded-full px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 @[760px]:px-6 ${active ? "" : "hover:bg-gray-100"} ${className}`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title && (
          <span title={title} className="truncate text-xs font-semibold leading-4 text-gray-900">
            {title}
            {titleDetail && <span className="font-medium text-gray-500"> · {titleDetail}</span>}
          </span>
        )}
        <span
          title={value ?? undefined}
          className={`truncate text-sm font-medium ${value ? (byChat ? "text-indigo-700" : "text-gray-900") : "text-gray-500"}`}
        >
          {value ?? placeholder}
        </span>
      </span>
      {trailing}
    </button>
  );
  // Phones edit in a bottom sheet with room for the rows and the keyboard.
  if (isMobile) {
    return (
      <>
        {trigger}
        <FetchModal isOpen={open} onClose={() => onOpenChange(false)} title={title ?? "Trip options"} mobileSheet>
          <div className={`flex flex-col gap-4 ${SURFACE_FEEL}`}>{children}</div>
        </FetchModal>
      </>
    );
  }
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={10}
          collisionPadding={12}
          className="z-50 outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <motion.div
            initial={reduceMotion ? false : POPOVER_MOTION.initial}
            animate={POPOVER_MOTION.animate}
            transition={POPOVER_MOTION.transition}
            style={{
              transformOrigin: "var(--radix-popover-content-transform-origin)",
              ...(panelWidth ? { width: panelWidth } : {}),
            }}
            className={`flex max-w-[calc(100vw-24px)] flex-col gap-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-xl ${SURFACE_FEEL} ${panelWidth ? "" : panelClass}`}
          >
            {children}
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});
